// Automation.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import ModuleSidebar from '../../components/ModuleSidebar/ModuleSidebar';
import './Automation.css';
import {
  TbCloud,
  TbTrash,
  TbArrowsMaximize,
  TbPlayerPlay,
  TbSettings,
  TbCheck,
  TbX,
  TbLink,
  TbTerminal2
} from 'react-icons/tb';
import Services from '../../services/Services';

/* =========================
   Node arrangement algorithm
========================= */
const arrangeNodesInGrid = (nodes, edges) => {
  if (nodes.length === 0) return [];
  const GRID_SPACING_X = 250;
  const GRID_SPACING_Y = 100;
  const START_X = 100;
  const START_Y = 100;
  const ORPHAN_START_Y = 400;

  const incomingCounts = {};
  const outgoingEdges = {};
  const allNodeIds = new Set(nodes.map(n => n.id));

  nodes.forEach(n => {
    incomingCounts[n.id] = 0;
    outgoingEdges[n.id] = [];
  });
  edges.forEach(e => {
    if (allNodeIds.has(e.targetNodeId)) incomingCounts[e.targetNodeId] += 1;
    if (allNodeIds.has(e.sourceNodeId)) outgoingEdges[e.sourceNodeId].push(e.targetNodeId);
  });

  const layers = [];
  const visited = new Set();
  const roots = nodes.filter(n => incomingCounts[n.id] === 0);
  if (roots.length) {
    layers.push(roots.map(n => n.id));
    roots.forEach(n => visited.add(n.id));
  }

  let i = 0;
  while (i < layers.length) {
    const next = new Set();
    layers[i].forEach(id => {
      (outgoingEdges[id] || []).forEach(tid => {
        if (!visited.has(tid)) {
          const incoming = edges.filter(e => e.targetNodeId === tid);
          const ok = incoming.every(e => visited.has(e.sourceNodeId));
          if (ok) {
            next.add(tid);
            visited.add(tid);
          }
        }
      });
    });
    const arr = Array.from(next);
    if (arr.length) layers.push(arr);
    i++;
  }

  const layered = new Set(layers.flat());
  const unlayered = nodes.filter(n => !layered.has(n.id));

  const pos = {};
  layers.forEach((layerIds, layerIdx) => {
    const x = START_X + layerIdx * GRID_SPACING_X;
    const groups = {};
    layerIds.forEach(id => {
      const inc = edges.filter(e => e.targetNodeId === id);
      const key = inc.length ? inc[0].sourceNodeId : 'root';
      (groups[key] ??= []).push(id);
    });
    let row = 0;
    Object.values(groups).forEach(ids => {
      const yStart = START_Y + row * GRID_SPACING_Y;
      ids.forEach((id, k) => {
        pos[id] = { x, y: yStart + k * GRID_SPACING_Y };
      });
      row += Math.max(ids.length, 1) + 1;
    });
  });

  unlayered.forEach((n, idx) => {
    pos[n.id] = { x: START_X, y: ORPHAN_START_Y + idx * GRID_SPACING_Y };
  });

  return nodes.map(n =>
    pos[n.id] ? { ...n, ...pos[n.id] } : { ...n, x: START_X, y: START_Y }
  );
};

const safeStringify = obj => {
  try {
    return JSON.stringify(obj, null, 2);
  } catch (e) {
    return String(obj);
  }
};
const nodeLabel = n => `${n?.type ?? 'Unknown'} [${n?.id ?? '??'}]`;

const isUserTyping = () => {
  const ae = document.activeElement;
  if (!ae) return false;

  if (ae.closest && ae.closest('.parameter-modal')) return true;
  if (ae.closest && ae.closest('.workflow-modal')) return true;

  const tag = ae.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    ae.isContentEditable === true
  );
};

const getNestedValue = (obj, path) => {
  if (!path.startsWith('$.result.output.')) return undefined;
  const cleanPath = path.substring('$.result.output.'.length);
  const parts = cleanPath.split(/\.|\[|\]\.?/).filter(Boolean);
  let current = obj;
  for (const part of parts) {
    if (current == null) return undefined;
    current = current[part];
  }
  return current;
};

// $.result.output içinden tüm leaf path'leri çıkar
const buildOutputPaths = output => {
  const items = [];
  const walk = (value, path) => {
    if (value !== null && typeof value === 'object') {
      if (Array.isArray(value)) {
        value.forEach((v, i) => walk(v, `${path}[${i}]`));
      } else {
        Object.entries(value).forEach(([k, v]) => walk(v, `${path}.${k}`));
      }
    } else {
      items.push({ path, value });
    }
  };
  walk(output, '$.result.output');
  return items;
};

/* =========================
   Workflow schema helpers
========================= */
const buildWorkflowSchema = ({ nodes, edges, zoom }) => {
  // kendi formatımız
  return {
    version: 1,
    canvas: {
      zoom: typeof zoom === 'number' ? zoom : 0.8
    },
    nodes: (nodes || []).map(n => ({
      id: n.id,
      type: n.type,
      x: n.x,
      y: n.y,
      properties: n.properties ?? {},
      parameters: n.parameters ?? {},
      dataMappings: n.dataMappings ?? {}
    })),
    edges: (edges || []).map(e => ({
      id: e.id,
      sourceNodeId: e.sourceNodeId,
      targetNodeId: e.targetNodeId
    }))
  };
};

const parseWorkflowSchema = schema => {
  // Bizim formatımızı destekle, ama başka JSON gelirse de "mantıklı" çekmeye çalış.
  if (!schema || typeof schema !== 'object') {
    return { nodes: [], edges: [], zoom: 0.8 };
  }

  // v1 format
  if (schema.version === 1 && Array.isArray(schema.nodes) && Array.isArray(schema.edges)) {
    const nodes = schema.nodes.map(n => ({
      id: n.id,
      type: n.type,
      x: Number(n.x ?? 100),
      y: Number(n.y ?? 100),
      properties: n.properties ?? {},
      parameters: n.parameters ?? {},
      dataMappings: n.dataMappings ?? {}
    }));
    const edges = schema.edges.map(e => ({
      id: e.id || `edge-${e.sourceNodeId}-${e.targetNodeId}-${Date.now()}`,
      sourceNodeId: e.sourceNodeId,
      targetNodeId: e.targetNodeId
    }));
    const zoom = Number(schema?.canvas?.zoom ?? 0.8);
    return { nodes, edges, zoom: isNaN(zoom) ? 0.8 : zoom };
  }

  // örnek format (node1,node2 + edges: {source,target})
  if (Array.isArray(schema.nodes) && Array.isArray(schema.edges)) {
    const nodes = schema.nodes.map(n => ({
      id: String(n.id),
      type: n.type || 'node',
      x: Number(n.x ?? 100),
      y: Number(n.y ?? 100),
      properties: n.properties ?? n.data ?? {},
      parameters: n.parameters ?? {},
      dataMappings: n.dataMappings ?? {}
    }));
    const edges = schema.edges.map(e => ({
      id: e.id || `edge-${e.source || e.sourceNodeId}-${e.target || e.targetNodeId}-${Date.now()}`,
      sourceNodeId: e.sourceNodeId || e.source,
      targetNodeId: e.targetNodeId || e.target
    }));
    return { nodes, edges, zoom: 0.8 };
  }

  return { nodes: [], edges: [], zoom: 0.8 };
};

const stableStringify = obj => {
  // basit “hash”
  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
};

export default function Automation() {
  const token = localStorage.getItem('token');

  /* =========================
     Terminal logs
  ========================= */
  const [logs, setLogs] = useState([]);
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalPos, setTerminalPos] = useState({ x: 40, y: 80 });
  const [terminalSize, setTerminalSize] = useState({ width: 520, height: 260 });
  const terminalBodyRef = useRef(null);
  const isDraggingTerminal = useRef(false);
  const terminalDragOffset = useRef({ x: 0, y: 0 });

  const isResizingTerminal = useRef(false);
  const terminalResizeDir = useRef(null);
  const terminalResizeStart = useRef({
    mouseX: 0,
    mouseY: 0,
    width: 0,
    height: 0,
    x: 0,
    y: 0
  });

  const executionResultsRef = useRef({});

  const appendLog = (level, label, data) => {
    setLogs(prev => {
      const time = new Date().toLocaleTimeString();
      const entry = {
        id: `${Date.now()}-${Math.random()}`,
        time,
        level: String(level || 'INFO').toUpperCase(),
        label,
        message:
          data === undefined
            ? ''
            : typeof data === 'string'
              ? data
              : safeStringify(data),
      };
      const next = [...prev, entry];
      if (next.length > 500) next.shift();
      return next;
    });
  };

  useEffect(() => {
    if (!terminalBodyRef.current) return;
    const el = terminalBodyRef.current;
    el.scrollTop = el.scrollHeight;
  }, [logs, showTerminal]);

  const handleTerminalMouseDown = e => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingTerminal.current = true;
    terminalDragOffset.current = {
      x: e.clientX - terminalPos.x,
      y: e.clientY - terminalPos.y
    };
  };

  const handleTerminalResizeMouseDown = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();

    isResizingTerminal.current = true;
    terminalResizeDir.current = direction;
    terminalResizeStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: terminalSize.width,
      height: terminalSize.height,
      x: terminalPos.x,
      y: terminalPos.y
    };
  };

  /* =========================
     Canvas / Nodes
  ========================= */
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [zoom, setZoom] = useState(0.8);
  const [selectedNodeIds, setSelectedNodeIds] = useState(new Set());
  const [selectedEdgeIds, setSelectedEdgeIds] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState(null);
  const isConnecting = useRef(false);
  const connectingSourceNodeId = useRef(null);
  const draggingNodeId = useRef(null);
  const isDragging = useRef(false);
  const initialMousePos = useRef({ x: 0, y: 0 });
  const initialNodePositions = useRef({});
  const canvasRef = useRef(null);
  const connectionLineRef = useRef(null);
  const [nodeErrors, setNodeErrors] = useState({});
  const [nodeParamErrors, setNodeParamErrors] = useState({});

  /* =========================
     Service Nodes (sidebar)
  ========================= */
  const [serviceNodes, setServiceNodes] = useState([]);
  const [isLoadingService, setIsLoadingService] = useState(false);
  const [serviceError, setServiceError] = useState(null);

  /* =========================
     Sidebar state
  ========================= */
  const [selectedSubmenu, setSelectedSubmenu] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('');

  /* =========================
     Execution state
  ========================= */
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState({});
  const [executionError, setExecutionError] = useState(null);

  /* =========================
     Node parameter modal state
  ========================= */
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [currentNodeParams, setCurrentNodeParams] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [dataMappings, setDataMappings] = useState({});
  const [executingNodeId, setExecutingNodeId] = useState(null);

  /* =========================
     Workflow CRUD state
  ========================= */
  const [workflowID, setWorkflowID] = useState(null);
  const [workflowTitle, setWorkflowTitle] = useState('');
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const [workflowList, setWorkflowList] = useState([]);
  const [workflowListLoading, setWorkflowListLoading] = useState(false);
  const [workflowListError, setWorkflowListError] = useState(null);

  const [workflowContextMenu, setWorkflowContextMenu] = useState({
    open: false,
    x: 0,
    y: 0,
    workflow: null
  });

  const isLoadingWorkflowRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const lastSavedHashRef = useRef('');
  const lastSavedTitleRef = useRef('');
  const autosaveSkipOnceRef = useRef(true);

  // ✅ Title click-to-edit
  const [isTitleEditing, setIsTitleEditing] = useState(false);

  // ✅ Autosave spinner (notebook ile aynı)
  const [isAutosaving, setIsAutosaving] = useState(false);

  const saveToHistory = (newNodes, newEdges) => {
    const h = history.slice(0, historyIndex + 1);
    h.push({
      nodes: JSON.parse(JSON.stringify(newNodes ?? nodes)),
      edges: JSON.parse(JSON.stringify(newEdges ?? edges))
    });
    setHistory(h);
    setHistoryIndex(h.length - 1);
  };

  const arrangeNodes = () => {
    if (!nodes.length) return;
    const arranged = arrangeNodesInGrid(nodes, edges);
    setNodes(arranged);
    saveToHistory(arranged, edges);
  };

  const getMousePosition = e => {
    if (!canvasRef.current) return { x: 0, y: 0 };
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) / zoom, y: (e.clientY - r.top) / zoom };
  };

  /* =========================
     Terminal drag/resize listeners
  ========================= */
  useEffect(() => {
    const onMove = e => {
      if (isResizingTerminal.current) {
        const { mouseX, mouseY, width, height, x, y } = terminalResizeStart.current;
        const dx = e.clientX - mouseX;
        const dy = e.clientY - mouseY;
        const dir = terminalResizeDir.current;

        const minWidth = 320;
        const minHeight = 160;

        let newWidth = width;
        let newHeight = height;
        let newX = x;
        let newY = y;

        if (dir.includes('right')) newWidth = Math.max(minWidth, width + dx);
        if (dir.includes('left')) {
          newWidth = Math.max(minWidth, width - dx);
          newX = x + dx;
        }
        if (dir.includes('bottom')) newHeight = Math.max(minHeight, height + dy);
        if (dir.includes('top')) {
          newHeight = Math.max(minHeight, height - dy);
          newY = y + dy;
        }

        setTerminalSize({ width: newWidth, height: newHeight });
        setTerminalPos({ x: newX, y: newY });
        return;
      }

      if (!isDraggingTerminal.current) return;
      setTerminalPos({
        x: e.clientX - terminalDragOffset.current.x,
        y: e.clientY - terminalDragOffset.current.y
      });
    };

    const onUp = () => {
      if (isDraggingTerminal.current) isDraggingTerminal.current = false;
      if (isResizingTerminal.current) {
        isResizingTerminal.current = false;
        terminalResizeDir.current = null;
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [terminalPos.x, terminalPos.y, terminalSize.width, terminalSize.height]);

  /* =========================
     Port constants
  ========================= */
  const NODE_PORT_CENTER_Y = 30;
  const NODE_OUTPUT_X_OFFSET = 170;
  const NODE_INPUT_X_OFFSET = -4;

  const getNodeCenter = n => ({ x: n.x + 60, y: n.y + NODE_PORT_CENTER_Y });
  const getNodeOutputPosition = n => ({ x: n.x + NODE_OUTPUT_X_OFFSET, y: n.y + NODE_PORT_CENTER_Y });
  const getNodeInputPosition = n => ({ x: n.x + NODE_INPUT_X_OFFSET, y: n.y + NODE_PORT_CENTER_Y });

  /* =========================
     Canvas interactions
  ========================= */
  const handleCanvasMouseDown = e => {
    if (e.button !== 0) return;
    const m = getMousePosition(e);
    initialMousePos.current = m;

    if (e.target === canvasRef.current || e.target.classList.contains('nodes-wrapper')) {
      setIsSelecting(true);
      setSelectionBox({ startX: m.x, startY: m.y, endX: m.x, endY: m.y });
      if (!e.ctrlKey && !e.metaKey) {
        setSelectedNodeIds(new Set());
        setSelectedEdgeIds(new Set());
      }
    }
  };

  const handleNodeOutputMouseDown = (e, nodeId) => {
    e.stopPropagation();
    isConnecting.current = true;
    connectingSourceNodeId.current = nodeId;
    const n = nodes.find(nn => nn.id === nodeId);
    const p = getNodeOutputPosition(n);
    if (connectionLineRef.current) {
      connectionLineRef.current.style.display = 'block';
      connectionLineRef.current.setAttribute('x1', p.x);
      connectionLineRef.current.setAttribute('y1', p.y);
      connectionLineRef.current.setAttribute('x2', p.x);
      connectionLineRef.current.setAttribute('y2', p.y);
    }
  };

  const handleMouseDown = (e, node) => {
    e.preventDefault();
    e.stopPropagation();
    if (isConnecting.current) return;

    const m = getMousePosition(e);

    if (e.ctrlKey || e.metaKey) {
      setSelectedNodeIds(prev => {
        const s = new Set(prev);
        s.has(node.id) ? s.delete(node.id) : s.add(node.id);
        return s;
      });
    } else if (!selectedNodeIds.has(node.id)) {
      setSelectedNodeIds(new Set([node.id]));
      setSelectedEdgeIds(new Set());
    }

    draggingNodeId.current = node.id;
    isDragging.current = true;
    initialNodePositions.current = {};

    nodes.forEach(n => {
      if (selectedNodeIds.has(n.id) || n.id === node.id) {
        initialNodePositions.current[n.id] = { x: n.x, y: n.y };
      }
    });

    initialMousePos.current = m;
  };

  const handleMouseMove = e => {
    const m = getMousePosition(e);

    if (isConnecting.current && connectionLineRef.current) {
      const src = nodes.find(n => n.id === connectingSourceNodeId.current);
      if (src) {
        const p = getNodeOutputPosition(src);
        connectionLineRef.current.setAttribute('x1', p.x);
        connectionLineRef.current.setAttribute('y1', p.y);
        connectionLineRef.current.setAttribute('x2', m.x);
        connectionLineRef.current.setAttribute('y2', m.y);
      }
    }

    if (isSelecting && selectionBox) {
      setSelectionBox(sb => ({ ...sb, endX: m.x, endY: m.y }));
      return;
    }

    if (isDragging.current && draggingNodeId.current) {
      const dx = m.x - initialMousePos.current.x;
      const dy = m.y - initialMousePos.current.y;

      setNodes(prev =>
        prev.map(n => {
          if (selectedNodeIds.has(n.id) || n.id === draggingNodeId.current) {
            const init = initialNodePositions.current[n.id];
            if (init) return { ...n, x: init.x + dx, y: init.y + dy };
          }
          return n;
        })
      );
    }
  };

  const handleNodeInputMouseUp = (e, targetNodeId) => {
    e.stopPropagation();
    if (
      isConnecting.current &&
      connectingSourceNodeId.current &&
      connectingSourceNodeId.current !== targetNodeId
    ) {
      const exists = edges.find(
        ed => ed.sourceNodeId === connectingSourceNodeId.current && ed.targetNodeId === targetNodeId
      );
      if (!exists) {
        const newEdge = {
          id: `edge-${Date.now()}`,
          sourceNodeId: connectingSourceNodeId.current,
          targetNodeId
        };
        const newEdges = [...edges, newEdge];
        setEdges(newEdges);
        saveToHistory(nodes, newEdges);
      }
    }
    isConnecting.current = false;
    connectingSourceNodeId.current = null;
    if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
  };

  const handleMouseUp = e => {
    if (
      isConnecting.current &&
      (e.target === canvasRef.current || e.target.classList.contains('nodes-wrapper'))
    ) {
      isConnecting.current = false;
      connectingSourceNodeId.current = null;
      if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
    }

    if (isSelecting && selectionBox) {
      const { startX, startY, endX, endY } = selectionBox;
      const minX = Math.min(startX, endX);
      const minY = Math.min(startY, endY);
      const maxX = Math.max(startX, endX);
      const maxY = Math.max(startY, endY);

      const nSel = nodes
        .filter(n => n.x >= minX && n.x <= maxX && n.y >= minY && n.y <= maxY)
        .map(n => n.id);

      const eSel = edges
        .filter(ed => {
          const s = nodes.find(n => n.id === ed.sourceNodeId);
          const t = nodes.find(n => n.id === ed.targetNodeId);
          if (!s || !t) return false;
          const sc = getNodeCenter(s);
          const tc = getNodeCenter(t);
          const midX = (sc.x + tc.x) / 2;
          const midY = (sc.y + tc.y) / 2;
          return midX >= minX && midX <= maxX && midY >= minY && midY <= maxY;
        })
        .map(ed => ed.id);

      setSelectedNodeIds(prev => {
        const s = new Set(prev);
        nSel.forEach(id => s.add(id));
        return s;
      });
      setSelectedEdgeIds(prev => {
        const s = new Set(prev);
        eSel.forEach(id => s.add(id));
        return s;
      });

      setIsSelecting(false);
      setSelectionBox(null);
    }

    if (isDragging.current) {
      isDragging.current = false;
      draggingNodeId.current = null;
      saveToHistory(nodes, edges);
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [zoom, isSelecting, selectionBox, selectedNodeIds, selectedEdgeIds, nodes, edges]);

  const handleWheel = e => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.min(Math.max(prev + delta, 0.3), 1));
  };

  /* =========================
     Keyboard shortcuts
  ========================= */
  useEffect(() => {
    const onKey = e => {
      const typing = isUserTyping();

      // context menu açıkken Escape ile kapat
      if (e.key === 'Escape') {
        setWorkflowContextMenu({ open: false, x: 0, y: 0, workflow: null });

        setSelectedNodeIds(new Set());
        setSelectedEdgeIds(new Set());
        setIsSelecting(false);
        setSelectionBox(null);
        isConnecting.current = false;
        connectingSourceNodeId.current = null;
        if (connectionLineRef.current) connectionLineRef.current.style.display = 'none';
        return;
      }

      if ((e.key === 'Delete' || e.key === 'Backspace') && typing) return;
      if ((e.ctrlKey || e.metaKey) && typing) {
        if (['z', 'y', 'c', 'v', 'x', 'a'].includes(e.key.toLowerCase())) return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeIds.size) {
          const n2 = nodes.filter(n => !selectedNodeIds.has(n.id));
          const e2 = edges.filter(
            ed => !selectedNodeIds.has(ed.sourceNodeId) && !selectedNodeIds.has(ed.targetNodeId)
          );
          setNodes(n2);
          setEdges(e2);
          setSelectedNodeIds(new Set());
          saveToHistory(n2, e2);
        } else if (selectedEdgeIds.size) {
          const e2 = edges.filter(ed => !selectedEdgeIds.has(ed.id));
          setEdges(e2);
          setSelectedEdgeIds(new Set());
          saveToHistory(nodes, e2);
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if (historyIndex > 0) {
          const prev = history[historyIndex - 1];
          setNodes(prev.nodes);
          setEdges(prev.edges);
          setHistoryIndex(historyIndex - 1);
        }
      }

      if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        if (historyIndex < history.length - 1) {
          const next = history[historyIndex + 1];
          setNodes(next.nodes);
          setEdges(next.edges);
          setHistoryIndex(historyIndex + 1);
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
        if (selectedNodeIds.size && !typing) {
          const selected = nodes.filter(n => selectedNodeIds.has(n.id));
          localStorage.setItem('copiedNodes', JSON.stringify(selected));
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (typing) return;
        const copied = localStorage.getItem('copiedNodes');
        if (copied) {
          try {
            const parsed = JSON.parse(copied);
            const n2 = [...nodes];
            const ids = new Set();
            parsed.forEach(n => {
              const nn = {
                ...n,
                id: `${n.id}-copy-${Date.now()}`,
                x: n.x + 20,
                y: n.y + 20
              };
              n2.push(nn);
              ids.add(nn.id);
            });
            setNodes(n2);
            setSelectedNodeIds(ids);
            saveToHistory(n2, edges);
          } catch (err) {
            console.error('Paste error:', err);
          }
        }
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nodes, edges, selectedNodeIds, selectedEdgeIds, history, historyIndex]);

  /* =========================
     Fetch service nodes (sidebar)
  ========================= */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoadingService(true);
      setServiceError(null);
      try {
        const res = await Services.AutomationGetNodes(token);
        const list = Array.isArray(res?.data?.result) ? res.data.result : [];
        const normalized = list
          .filter(it => String(it.isVisible) === '1' && String(it.status) === '1')
          .sort((a, b) => parseInt(a.numeric_index || '0', 10) - parseInt(b.numeric_index || '0', 10));
        if (mounted) setServiceNodes(normalized);
      } catch (e) {
        console.error('AutomationGetNodes error:', e);
        if (mounted) {
          setServiceNodes([]);
          setServiceError('Node listesi alınamadı');
        }
      } finally {
        if (mounted) setIsLoadingService(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  /* =========================
     Build ModuleSidebar menus
  ========================= */
  const serviceSections = useMemo(() => {
    if (!serviceNodes.length) return [];

    const catMap = new Map();
    for (const def of serviceNodes) {
      const cat = def.category || 'Services';
      const sub = def.subCategory || 'General';
      if (!catMap.has(cat)) catMap.set(cat, new Map());
      const subMap = catMap.get(cat);
      if (!subMap.has(sub)) subMap.set(sub, []);
      subMap.get(sub).push(def);
    }

    const sections = [];
    for (const [cat, subMap] of catMap.entries()) {
      const section = {
        id: `section-service-${cat.toLowerCase().replace(/\s+/g, '-')}`,
        title: cat,
        icon: <TbCloud size={18} />,
        showEntityButton: false,
        submenu: []
      };

      for (const [sub, defs] of subMap.entries()) {
        const onlyGeneral = subMap.size === 1 && sub === 'General';
        if (onlyGeneral) {
          section.submenu.push(
            ...defs.map(def => ({
              id: def.nodeID,
              title: def.title || def.nodeHandle,
              serviceDef: def
            }))
          );
        } else {
          section.submenu.push({
            id: `group-${cat}-${sub}`.toLowerCase().replace(/\s+/g, '-'),
            title: sub,
            nestedSubmenu: defs.map(def => ({
              id: def.nodeID,
              title: def.title || def.nodeHandle,
              serviceDef: def
            }))
          });
        }
      }

      sections.push(section);
    }
    return sections;
  }, [serviceNodes]);

  const menus = useMemo(() => [{ sections: serviceSections }], [serviceSections]);

  /* =========================
     Add node from sidebar
  ========================= */
  const handleSubmenuClick = item => {
    const def = item?.serviceDef;
    if (!def) return;

    const newNode = {
      id: `${def.nodeHandle}-${Date.now()}`,
      type: def.title || def.nodeHandle,
      x: 100 + (nodes.length % 6) * 40,
      y: 100 + Math.floor(nodes.length / 6) * 40,
      properties: {
        nodeID: def.nodeID,
        nodeHandle: def.nodeHandle,
        category: def.category,
        subCategory: def.subCategory,
        nodeType: def.nodeType,
        nodeParameters: def.nodeParameters,
        version: def.versionString,
        url: def.url
      },
      parameters: {},
      dataMappings: {}
    };

    const ns = [...nodes, newNode];
    setNodes(ns);
    setSelectedNodeIds(new Set([newNode.id]));
    saveToHistory(ns, edges);
  };

  /* =========================
     Node parameter modal open
  ========================= */
  const handleNodeParameterEdit = node => {
    setCurrentNode(node);
    setCurrentNodeParams(node.parameters || {});
    setDataMappings(node.dataMappings || {});
    setShowParameterModal(true);
  };

  const updateNodeParameters = (nodeId, newParams) => {
    setNodes(prev => prev.map(n => (n.id === nodeId ? { ...n, parameters: newParams } : n)));
    setCurrentNodeParams(newParams);
  };

  const updateDataMappings = (nodeId, newMappings) => {
    setNodes(prev => prev.map(n => (n.id === nodeId ? { ...n, dataMappings: newMappings } : n)));
    setDataMappings(newMappings);
  };

  const handleParamChange = (key, rawValue) => {
    if (!currentNode) return;

    const isMapping = typeof rawValue === 'string' && rawValue.trim().startsWith('$.');

    if (isMapping) {
      const newMappings = { ...dataMappings, [key]: rawValue };
      const newParams = { ...(currentNodeParams || {}) };
      delete newParams[key];

      updateDataMappings(currentNode.id, newMappings);
      updateNodeParameters(currentNode.id, newParams);
    } else {
      const newParams = { ...(currentNodeParams || {}), [key]: rawValue };
      const newMappings = { ...(dataMappings || {}) };
      delete newMappings[key];

      updateNodeParameters(currentNode.id, newParams);
      updateDataMappings(currentNode.id, newMappings);
    }
  };

  /* =========================
     Drag mapping paths
  ========================= */
  const handlePathDragStart = (e, path) => {
    const payload = { path };
    e.dataTransfer.setData('application/json', JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', path);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleParamDragOver = e => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleParamDragEnter = e => {
    e.preventDefault();
    const group = e.currentTarget.closest('.parameter-input-group');
    if (group) group.classList.add('drag-over');
  };

  const handleParamDragLeave = e => {
    e.preventDefault();
    const group = e.currentTarget.closest('.parameter-input-group');
    if (group) group.classList.remove('drag-over');
  };

  const handleParamDrop = (e, key) => {
    e.preventDefault();
    const group = e.currentTarget.closest('.parameter-input-group');
    if (group) group.classList.remove('drag-over');

    let droppedPath = '';

    const json = e.dataTransfer.getData('application/json');
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (parsed.path) droppedPath = parsed.path;
      } catch (err) {
        console.warn('Drag payload parse error:', err);
      }
    }

    if (!droppedPath) droppedPath = e.dataTransfer.getData('text/plain');
    if (droppedPath) handleParamChange(key, droppedPath);
  };

  /* =========================
     Execution
  ========================= */
  const executeFlow = async () => {
    setIsExecuting(true);
    setNodeErrors({});
    setNodeParamErrors({});
    setExecutionError(null);
    setExecutionResults({});
    setExecutingNodeId(null);
    executionResultsRef.current = {}; // ⭐ önemli

    appendLog('INFO', 'Flow started');

    try {
      const rootNodes = nodes.filter(node => !edges.some(edge => edge.targetNodeId === node.id));

      appendLog('DEBUG', 'Root nodes:', rootNodes.map(n => nodeLabel(n)));

      for (const rootNode of rootNodes) {
        appendLog('INFO', `Root node starting → ${nodeLabel(rootNode)}`);
        await executeNodeFlow(rootNode.id, {}, { skipChildren: false });
      }

      appendLog('INFO', 'Flow completed successfully');
    } catch (error) {
      console.error('%c[Automation] ✖ Hata:', 'color:#dc143c;font-weight:bold;', error);
      appendLog('ERROR', 'Flow error:', error?.message || String(error));
      setExecutionError(error.message || 'Bir hata oluştu');
    } finally {
      setIsExecuting(false);
      setExecutingNodeId(null);
    }
  };

  const executeSingleNode = async nodeId => {
    setExecutionError(null);

    setNodeErrors(prev => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    setNodeParamErrors(prev => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });

    appendLog('INFO', `Single node run requested → ${nodeId}`);

    try {
      await executeNodeFlow(nodeId, {}, { skipChildren: true });
      appendLog('INFO', `Single node completed → ${nodeId}`);
    } catch (error) {
      console.error('%c[Automation] ✖ Node run error:', 'color:#dc143c;font-weight:bold;', error);
      appendLog('ERROR', `Single node run error → ${nodeId}`, error?.message || String(error));
      setExecutionError(error.message || 'Bir hata oluştu');
    } finally {
      setExecutingNodeId(null);
    }
  };

  const executeNodeFlow = async (nodeId, inputData, options = {}) => {
    const { skipChildren = false } = options;

    const node = nodes.find(n => n.id === nodeId);
    if (!node) {
      console.warn('[Automation] Node bulunamadı:', nodeId);
      appendLog('WARN', `Node not found: ${nodeId}`);
      return;
    }

    // Bu node yeniden çalıştırılırken eski hata kayıtlarını temizle
    setNodeErrors(prev => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });
    setNodeParamErrors(prev => {
      const next = { ...prev };
      delete next[nodeId];
      return next;
    });

    setExecutingNodeId(nodeId);

    appendLog('INFO', `Node input → ${nodeLabel(node)}`, inputData);

    // 1) Parametreleri type'ına göre hazırla
    let requestData = {};
    if (node.parameters) {
      Object.entries(node.parameters).forEach(([key, value]) => {
        const paramDef = node.properties?.nodeParameters?.[key];

        if (!paramDef || !paramDef.type) {
          requestData[key] = value;
          return;
        }

        const { type, default: defaultValue } = paramDef;

        switch (type) {
          case 'number': {
            const num = Number(value);
            requestData[key] = isNaN(num) && defaultValue !== undefined ? defaultValue : num || 0;
            break;
          }
          case 'integer': {
            const int = Number(value);
            requestData[key] =
              isNaN(int) || !Number.isInteger(int)
                ? defaultValue !== undefined
                  ? defaultValue
                  : 0
                : int;
            break;
          }
          case 'boolean': {
            if (typeof value === 'boolean') {
              requestData[key] = value;
            } else if (typeof value === 'string') {
              const lower = value.trim().toLowerCase();
              requestData[key] = !(lower === 'false' || lower === '0' || lower === '');
            } else {
              requestData[key] = Boolean(value);
            }
            break;
          }
          case 'string':
            requestData[key] = String(value ?? '');
            break;
          case 'array':
            if (Array.isArray(value)) {
              requestData[key] = value;
            } else if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                requestData[key] = Array.isArray(parsed) ? parsed : [parsed];
              } catch {
                requestData[key] = [];
              }
            } else if (value == null) {
              requestData[key] = [];
            } else {
              requestData[key] = [value];
            }
            break;
          case 'object':
            if (value && typeof value === 'object' && !Array.isArray(value)) {
              requestData[key] = value;
            } else if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                requestData[key] = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
              } catch {
                requestData[key] = {};
              }
            } else {
              requestData[key] = {};
            }
            break;
          case 'json':
            if (typeof value === 'string') {
              try {
                requestData[key] = JSON.parse(value);
              } catch {
                requestData[key] = null;
              }
            } else {
              requestData[key] = value;
            }
            break;
          case 'null':
            requestData[key] = null;
            break;
          case 'any':
          default:
            requestData[key] = value;
            break;
        }
      });
    }

    // 2) Data Mapping uygula (varsa) ve eksik path'ler için hata üret
    const missingMappings = [];
    const incomingEdges = edges.filter(edge => edge.targetNodeId === nodeId);
    const dataMappingsForNode = node.dataMappings || {};

    for (const [targetParam, sourcePath] of Object.entries(dataMappingsForNode)) {
      let resolved = false;
      let lastSourceNodeId = null;

      for (const edge of incomingEdges) {
        const sourceNode = nodes.find(n => n.id === edge.sourceNodeId);
        if (!sourceNode) continue;

        const sourceResult = executionResultsRef.current[sourceNode.id];
        const sourceOutput = sourceResult?.result?.output;
        lastSourceNodeId = sourceNode.id;

        if (!sourceOutput) continue;

        let sourceValue;
        if (typeof sourcePath === 'string') {
          if (sourcePath.startsWith('$.result.output.')) {
            sourceValue = getNestedValue(sourceOutput, sourcePath);
          } else if (sourcePath.startsWith('$.output.')) {
            sourceValue = getNestedValue(
              sourceOutput,
              '$.result.output.' + sourcePath.substring('$.output.'.length)
            );
          }
        }

        if (sourceValue !== undefined) {
          requestData[targetParam] = sourceValue;
          resolved = true;
          break;
        }
      }

      if (!resolved && sourcePath) {
        missingMappings.push({
          targetParam,
          sourcePath,
          sourceNodeId: lastSourceNodeId
        });
      }
    }

    if (missingMappings.length > 0) {
      appendLog('WARN', `Missing mappings on ${nodeLabel(node)}`, missingMappings);

      setNodeErrors(prev => ({
        ...prev,
        [nodeId]:
          prev[nodeId] ||
          "Bazı mapping path'lerinden veri alınamadı. Parametre detaylarını kontrol edin."
      }));

      setNodeParamErrors(prev => {
        const next = { ...prev };
        const perNode = { ...(next[nodeId] || {}) };
        missingMappings.forEach(m => {
          perNode[m.targetParam] = `Kaynak verisi bulunamadı: ${m.sourcePath}`;
        });
        next[nodeId] = perNode;
        return next;
      });
    }

    // 3) HTTP isteği
    let result;

    try {
      if (!node.properties?.url) {
        appendLog('INFO', `Node has no URL, echoing input → ${nodeLabel(node)}`);
        result = { result: { output: requestData } };
      } else {
        appendLog('INFO', `HTTP request → ${nodeLabel(node)} URL: ${node.properties.url}`, { requestData });

        const response = await fetch(node.properties.url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ requestData })
        });

        result = await response.json();
      }
    } catch (error) {
      appendLog('ERROR', `HTTP error on ${nodeLabel(node)}`, error?.message || String(error));

      setNodeErrors(prev => ({
        ...prev,
        [nodeId]: error.message || 'Çalıştırma hatası'
      }));

      throw error;
    }

    // 4) Sonucu kaydet
    executionResultsRef.current[nodeId] = result;        // ⭐ senkron gerçek kaynak
    setExecutionResults({ ...executionResultsRef.current }); // UI için snapshot
    appendLog('INFO', `Node output ← ${nodeLabel(node)}`, result?.result?.output ?? result);

    // ⭐ status:false ise node'u hatalı işaretle VE akışı durdur
    if (result && result.status === false) {
      const msg = result.message || 'Node status:false döndü';
      appendLog('ERROR', `Node status:false → ${nodeLabel(node)}`, msg);
      setNodeErrors(prev => ({ ...prev, [nodeId]: msg }));
      throw new Error(msg);
    }

    // 5) Çocuk node'ları çalıştır
    if (!skipChildren) {
      const connectedNodes = edges
        .filter(edge => edge.sourceNodeId === nodeId)
        .map(edge => edge.targetNodeId);

      for (const connectedNodeId of connectedNodes) {
        const targetNode = nodes.find(n => n.id === connectedNodeId);
        appendLog('INFO', `Flow step: ${nodeLabel(node)} ➜ ${nodeLabel(targetNode)}`);
        await executeNodeFlow(connectedNodeId, result?.result?.output || {}, { skipChildren: false });
      }
    }
  };

  /* =========================
     Workflow CRUD - fetch list
  ========================= */
  const fetchWorkflows = async () => {
    setWorkflowListLoading(true);
    setWorkflowListError(null);
    try {
      const res = await Services.AutomationGetWorkflowsByUser(token);
      const list = Array.isArray(res?.data?.result) ? res.data.result : [];
      // numeric_index desc ya da createdDateTime desc
      const sorted = [...list].sort((a, b) => {
        const ai = parseInt(a.numeric_index || '0', 10);
        const bi = parseInt(b.numeric_index || '0', 10);
        if (!isNaN(ai) && !isNaN(bi) && bi !== ai) return bi - ai;
        const at = parseInt(a.createdDateTime || '0', 10);
        const bt = parseInt(b.createdDateTime || '0', 10);
        return bt - at;
      });
      setWorkflowList(sorted);
      return sorted;
    } catch (e) {
      console.error('AutomationGetWorkflowsByUser error:', e);
      setWorkflowList([]);
      setWorkflowListError('Workflow listesi alınamadı');
      return [];
    } finally {
      setWorkflowListLoading(false);
    }
  };

  useEffect(() => {
    if (workflowModalOpen) fetchWorkflows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowModalOpen]);

  const closeWorkflowContextMenu = () => {
    setWorkflowContextMenu({ open: false, x: 0, y: 0, workflow: null });
  };

  useEffect(() => {
    const onAnyClick = () => closeWorkflowContextMenu();
    window.addEventListener('click', onAnyClick);
    return () => window.removeEventListener('click', onAnyClick);
  }, []);

  const openWorkflow = async wf => {
    if (!wf?.workflowID) return;

    isLoadingWorkflowRef.current = true;
    autosaveSkipOnceRef.current = true;

    try {
      const schemaParsed = parseWorkflowSchema(wf.workflowSchema);

      setWorkflowID(wf.workflowID);
      setWorkflowTitle(wf.title || '');
      setNodes(schemaParsed.nodes);
      setEdges(schemaParsed.edges);
      setZoom(schemaParsed.zoom);

      setSelectedNodeIds(new Set());
      setSelectedEdgeIds(new Set());
      setHistory([]);
      setHistoryIndex(-1);

      // last saved snapshot
      const built = buildWorkflowSchema({ nodes: schemaParsed.nodes, edges: schemaParsed.edges, zoom: schemaParsed.zoom });
      lastSavedHashRef.current = stableStringify(built);
      lastSavedTitleRef.current = wf.title || '';

      appendLog('INFO', 'Workflow opened', { workflowID: wf.workflowID, title: wf.title });
      setWorkflowModalOpen(false);

      // title edit kapansın
      setIsTitleEditing(false);
    } finally {
      // bir tick sonra autosave aç
      setTimeout(() => {
        isLoadingWorkflowRef.current = false;
      }, 50);
    }
  };

  // ✅ Yeni workflow: boş canvas + default title, KOPYALAMA YOK
  const startNewWorkflow = () => {
    closeWorkflowContextMenu();
    setWorkflowModalOpen(false);

    setWorkflowID(null);
    setWorkflowTitle(`Untitled Workflow ${new Date().toLocaleString()}`);
    setNodes([]);
    setEdges([]);
    setZoom(0.8);

    setSelectedNodeIds(new Set());
    setSelectedEdgeIds(new Set());
    setHistory([]);
    setHistoryIndex(-1);

    // snapshot sıfırla (henüz kaydedilmedi)
    lastSavedHashRef.current = '';
    lastSavedTitleRef.current = '';

    autosaveSkipOnceRef.current = true;
    isLoadingWorkflowRef.current = false;

    // direkt edit moduna al
    setTimeout(() => setIsTitleEditing(true), 0);

    appendLog('INFO', 'New empty workflow started');
  };

  const createWorkflowFromCurrent = async () => {
    const title = (workflowTitle || '').trim() || `Untitled Workflow ${new Date().toLocaleString()}`;
    const schema = buildWorkflowSchema({ nodes, edges, zoom });

    appendLog('INFO', 'Create workflow requested', { title });

    try {
      const res = await Services.AutomationCreateWorkflow(token, title, schema);
      const ok = res?.data?.status === true;
      if (!ok) {
        throw new Error(res?.data?.message || 'Workflow oluşturulamadı');
      }

      // backend workflowID döndürmüyorsa listeyi refetch edip en üsttekini seçmek daha güvenli.
      const freshList = await fetchWorkflows();

      // Eğer response içinde workflowID döndüyse yakalayalım
      const newId =
        res?.data?.result?.workflowID ||
        res?.data?.result?.id ||
        res?.data?.workflowID ||
        null;

      if (newId) {
        setWorkflowID(String(newId));
      } else {
        // listeden bulmayı dene: title eşleşen en yeni
        const latest =
          (freshList || []).find(w => (w.title || '') === title) ||
          (freshList || [])[0] ||
          null;

        if (latest?.workflowID) setWorkflowID(latest.workflowID);
      }

      // last saved
      lastSavedHashRef.current = stableStringify(schema);
      lastSavedTitleRef.current = title;

      appendLog('INFO', 'Workflow created', { title, workflowID: newId || '(unknown)' });
      setWorkflowModalOpen(false);
    } catch (e) {
      console.error('AutomationCreateWorkflow error:', e);
      appendLog('ERROR', 'Create workflow failed', e?.message || String(e));
      alert(e?.message || 'Workflow oluşturulamadı');
    }
  };

  const deleteWorkflow = async (wfId) => {
    const id = wfId || workflowID;
    if (!id) return;

    const ok = window.confirm('Bu workflow silinsin mi?');
    if (!ok) return;

    appendLog('WARN', 'Delete workflow requested', { workflowID: id });

    try {
      const res = await Services.AutomationDeleteWorkflow(token, id);
      if (res?.data?.status !== true) {
        throw new Error(res?.data?.message || 'Workflow silinemedi');
      }

      appendLog('INFO', 'Workflow deleted', { workflowID: id });

      // Eğer açık workflow silindiyse editörü temizle
      if (String(id) === String(workflowID)) {
        setWorkflowID(null);
        setWorkflowTitle('');
        setNodes([]);
        setEdges([]);
        setZoom(0.8);
        setSelectedNodeIds(new Set());
        setSelectedEdgeIds(new Set());
        setHistory([]);
        setHistoryIndex(-1);
        lastSavedHashRef.current = '';
        lastSavedTitleRef.current = '';
        setIsTitleEditing(false);
      }

      await fetchWorkflows();
      closeWorkflowContextMenu();
      setWorkflowModalOpen(false);
    } catch (e) {
      console.error('AutomationDeleteWorkflow error:', e);
      appendLog('ERROR', 'Delete workflow failed', e?.message || String(e));
      alert(e?.message || 'Workflow silinemedi');
    }
  };

  const renameWorkflow = async (wf, newTitle) => {
    if (!wf?.workflowID) return;
    const title = (newTitle || '').trim();
    if (!title) return;

    appendLog('INFO', 'Rename workflow requested', { workflowID: wf.workflowID, title });

    try {
      const res = await Services.AutomationUpdateWorkflow(token, wf.workflowID, { title });
      if (res?.data?.status !== true) throw new Error(res?.data?.message || 'Workflow yeniden adlandırılamadı');

      // Eğer açık olan workflow ise state'i güncelle
      if (String(wf.workflowID) === String(workflowID)) {
        setWorkflowTitle(title);
        lastSavedTitleRef.current = title;
      }

      await fetchWorkflows();
      closeWorkflowContextMenu();
    } catch (e) {
      console.error('AutomationUpdateWorkflow (rename) error:', e);
      appendLog('ERROR', 'Rename workflow failed', e?.message || String(e));
      alert(e?.message || 'Workflow yeniden adlandırılamadı');
    }
  };

  const manualSaveWorkflow = async () => {
    if (!workflowID) {
      // create
      await createWorkflowFromCurrent();
      return;
    }

    const schema = buildWorkflowSchema({ nodes, edges, zoom });
    const title = (workflowTitle || '').trim();

    const updateData = {};
    if (title && title !== lastSavedTitleRef.current) updateData.title = title;
    const schemaHash = stableStringify(schema);
    if (schemaHash !== lastSavedHashRef.current) updateData.workflowSchema = schema;

    if (Object.keys(updateData).length === 0) {
      appendLog('DEBUG', 'Manual save skipped (no changes)');
      return;
    }

    appendLog('INFO', 'Manual save update requested', { workflowID, updateDataKeys: Object.keys(updateData) });

    try {
      const res = await Services.AutomationUpdateWorkflow(token, workflowID, updateData);
      if (res?.data?.status !== true) throw new Error(res?.data?.message || 'Kaydedilemedi');

      if (updateData.workflowSchema) lastSavedHashRef.current = schemaHash;
      if (updateData.title) lastSavedTitleRef.current = updateData.title;

      appendLog('INFO', 'Manual save success', { workflowID });
      await fetchWorkflows();
    } catch (e) {
      console.error('AutomationUpdateWorkflow error:', e);
      appendLog('ERROR', 'Manual save failed', e?.message || String(e));
      alert(e?.message || 'Kaydedilemedi');
    }
  };

  /* =========================
     Autosave (2s idle)
  ========================= */
  useEffect(() => {
    // workflow yoksa autosave yok
    if (!workflowID) return;
    // loading sırasında autosave yok
    if (isLoadingWorkflowRef.current) return;

    // ilk değişimde (workflow load sonrası) bir defa skip
    if (autosaveSkipOnceRef.current) {
      autosaveSkipOnceRef.current = false;
      return;
    }

    // executing sırasında autosave'i tetikleme
    if (isExecuting) return;

    // timer reset
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = setTimeout(async () => {
      try {
        const schema = buildWorkflowSchema({ nodes, edges, zoom });
        const title = (workflowTitle || '').trim();

        const updateData = {};
        const schemaHash = stableStringify(schema);

        if (title && title !== lastSavedTitleRef.current) updateData.title = title;
        if (schemaHash !== lastSavedHashRef.current) updateData.workflowSchema = schema;

        if (Object.keys(updateData).length === 0) return;

        appendLog('INFO', 'Autosave triggered', { workflowID, updateDataKeys: Object.keys(updateData) });

        setIsAutosaving(true); // ✅ spinner ON
        const res = await Services.AutomationUpdateWorkflow(token, workflowID, updateData);
        if (res?.data?.status !== true) throw new Error(res?.data?.message || 'Autosave başarısız');

        if (updateData.workflowSchema) lastSavedHashRef.current = schemaHash;
        if (updateData.title) lastSavedTitleRef.current = updateData.title;

        appendLog('INFO', 'Autosave success', { workflowID });
        // liste açık değilse bile, isim güncellemeleri için sessiz refetch iyi olur
        if (workflowModalOpen || updateData.title) fetchWorkflows();
      } catch (e) {
        console.error('Autosave error:', e);
        appendLog('ERROR', 'Autosave failed', e?.message || String(e));
      } finally {
        setIsAutosaving(false); // ✅ spinner OFF
      }
    }, 2000);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowID, workflowTitle, nodes, edges, zoom, isExecuting]);

  /* =========================
     Modal render (workflow list)
  ========================= */
  const renderWorkflowModal = () => {
    if (!workflowModalOpen) return null;

    return (
      <div className="workflow-modal-overlay">
        <div className="workflow-modal">
          <div className="workflow-modal-header">
            <h3>Workflowlarım</h3>
            <button
              className="workflow-modal-close"
              onClick={() => {
                closeWorkflowContextMenu();
                setWorkflowModalOpen(false);
              }}
              title="Kapat"
            >
              <TbX size={20} />
            </button>
          </div>

          <div className="workflow-modal-body">
            <div className="workflow-modal-actions">
              {/* ✅ Yeni Workflow: boş aç, kopyalama yok */}
              <button
                className="btn btn-primary workflow-create-btn"
                onClick={startNewWorkflow}
                title="Sıfır workflow aç (boş canvas). Kaydet deyince oluşturur."
              >
                <TbCloud size={16} /> Yeni Workflow
              </button>

              <button
                className="btn workflow-refresh-btn"
                onClick={fetchWorkflows}
                disabled={workflowListLoading}
              >
                {workflowListLoading ? 'Yükleniyor...' : 'Yenile'}
              </button>
            </div>

            {workflowListError && <div className="workflow-error">{workflowListError}</div>}

            <div className="workflow-list">
              {workflowListLoading && workflowList.length === 0 && (
                <div className="workflow-empty">Workflowlar yükleniyor...</div>
              )}

              {!workflowListLoading && workflowList.length === 0 && (
                <div className="workflow-empty">Henüz workflow yok.</div>
              )}

              {workflowList.map(wf => (
                <div
                  key={wf.workflowID}
                  className={`workflow-item ${String(workflowID) === String(wf.workflowID) ? 'active' : ''}`}
                  onClick={() => openWorkflow(wf)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setWorkflowContextMenu({
                      open: true,
                      x: e.clientX,
                      y: e.clientY,
                      workflow: wf
                    });
                  }}
                  title="Açmak için tıkla • Sağ tık: Rename / Delete"
                >
                  <div className="workflow-item-title">{wf.title || '(Başlıksız)'}</div>
                  <div className="workflow-item-sub">
                    ID: {wf.workflowID}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="workflow-modal-footer">
            <button
              className="btn"
              onClick={() => {
                closeWorkflowContextMenu();
                setWorkflowModalOpen(false);
              }}
            >
              Kapat
            </button>
          </div>
        </div>

        {/* Context menu */}
        {workflowContextMenu.open && workflowContextMenu.workflow && (
          <div
            className="workflow-context-menu"
            style={{ left: workflowContextMenu.x, top: workflowContextMenu.y }}
            onClick={e => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                const wf = workflowContextMenu.workflow;
                const current = wf?.title || '';
                const next = window.prompt('Yeni workflow adı:', current);
                if (next === null) {
                  closeWorkflowContextMenu();
                  return;
                }
                renameWorkflow(wf, next);
              }}
            >
              Rename
            </button>

            <button
              type="button"
              className="danger"
              onClick={() => {
                const wf = workflowContextMenu.workflow;
                deleteWorkflow(wf?.workflowID);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  /* =========================
     Parameter Modal render
  ========================= */
  const renderParameterModal = () => {
    if (!showParameterModal || !currentNode || !currentNode.properties?.nodeParameters) return null;

    const { nodeParameters } = currentNode.properties;
    const { inputs, ...params } = nodeParameters;
    const paramErrorsForCurrent = nodeParamErrors[currentNode.id] || {};

    const incomingEdgesForNode = edges.filter(edge => edge.targetNodeId === currentNode.id);

    const inputGroups = incomingEdgesForNode
      .map(edge => {
        const sourceNode = nodes.find(n => n.id === edge.sourceNodeId);
        if (!sourceNode) return null;
        const exec = executionResults[sourceNode.id];
        const output = exec?.result?.output ?? exec ?? null;
        const paths = output ? buildOutputPaths(output) : [];
        return { sourceNode, hasExecution: !!output, paths };
      })
      .filter(Boolean);

    const currentExecution = executionResults[currentNode.id];
    const currentOutput = currentExecution?.result?.output ?? currentExecution ?? null;

    return (
      <div className="parameter-modal-overlay">
        <div className="parameter-modal">
          <div className="parameter-modal-header">
            <h3>{currentNode.type} Parametreleri</h3>
            <button
              className="parameter-modal-close"
              onClick={() => setShowParameterModal(false)}
            >
              <TbX size={20} />
            </button>
          </div>

          <div className="parameter-modal-body">
            <div className="parameter-columns">
              {/* LEFT */}
              <div className="parameter-column parameter-column-left">
                <div className="parameter-section">
                  <h4>Gelen Veriler</h4>
                  {inputGroups.length === 0 && (
                    <div className="input-empty">Bu node'a bağlı önceki node bulunmuyor.</div>
                  )}

                  {inputGroups.map(group => (
                    <div key={group.sourceNode.id} className="input-group">
                      <div className="input-group-title">{nodeLabel(group.sourceNode)}</div>

                      {!group.hasExecution && (
                        <div className="input-empty small">Bu node henüz çalıştırılmamış.</div>
                      )}

                      {group.hasExecution && group.paths.length === 0 && (
                        <div className="input-empty small">Çıktı bulundu ama gösterilebilir leaf değer yok.</div>
                      )}

                      {group.hasExecution && group.paths.length > 0 && (
                        <div className="input-path-list">
                          {group.paths.map((item, idx) => (
                            <div
                              key={`${group.sourceNode.id}-${idx}`}
                              className="input-path-item"
                              draggable
                              onDragStart={e => handlePathDragStart(e, item.path)}
                            >
                              <div className="input-path-path">{item.path}</div>
                              <div className="input-path-value">{String(item.value)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* MIDDLE */}
              <div className="parameter-column parameter-column-middle">
                <div className="parameter-section">
                  <h4>Parametreler</h4>
                  {Object.entries(params).length === 0 && (
                    <div className="input-empty small">Bu node için parametre tanımı yok.</div>
                  )}

                  {Object.entries(params).map(([key, param]) => {
                    const isBoolean = param.type === 'boolean';
                    const mappingValue = dataMappings?.[key];
                    const staticValue = currentNodeParams?.[key];
                    const paramError = paramErrorsForCurrent[key];

                    const isMappingActive =
                      typeof mappingValue === 'string' && mappingValue.trim().startsWith('$.');

                    const displayValue =
                      mappingValue !== undefined
                        ? mappingValue
                        : staticValue !== undefined
                          ? isBoolean
                            ? String(staticValue)
                            : staticValue
                          : '';

                    const placeholder =
                      param.type === 'array' || param.type === 'object' || param.type === 'json'
                        ? `${key} değeri veya path (örn: $.result.output.data[0])`
                        : isBoolean
                          ? `true / false veya path (örn: $.result.output.isOk)`
                          : `Değer veya path (örn: $.result.output.chatInput)`;

                    const handleChange = e => {
                      const raw = e.target.value;

                      if (isBoolean) {
                        const trimmed = raw.trim();
                        if (trimmed.startsWith('$.')) {
                          handleParamChange(key, trimmed);
                          return;
                        }
                        if (trimmed === '') {
                          handleParamChange(key, '');
                          return;
                        }
                        const lower = trimmed.toLowerCase();
                        if (lower === 'true') {
                          handleParamChange(key, true);
                          return;
                        }
                        if (lower === 'false') {
                          handleParamChange(key, false);
                          return;
                        }
                        handleParamChange(key, raw);
                        return;
                      }

                      if (param.type === 'number' || param.type === 'integer') {
                        if (raw === '') handleParamChange(key, '');
                        else {
                          const num = Number(raw);
                          handleParamChange(key, isNaN(num) ? '' : num);
                        }
                        return;
                      }

                      handleParamChange(key, raw);
                    };

                    const commonDropProps = {
                      onDrop: e => handleParamDrop(e, key),
                      onDragOver: handleParamDragOver,
                      onDragEnter: handleParamDragEnter,
                      onDragLeave: handleParamDragLeave
                    };

                    return (
                      <div
                        key={key}
                        className={`parameter-input-group ${isMappingActive ? 'mapping-active' : ''}`}
                      >
                        <label htmlFor={`param-${key}`}>
                          {key}
                          {param.isRequired && <span className="required">*</span>}
                          {param.description && (
                            <div className="param-description">{param.description}</div>
                          )}
                        </label>

                        {param.type === 'array' || param.type === 'object' || param.type === 'json' ? (
                          <textarea
                            id={`param-${key}`}
                            value={
                              typeof displayValue === 'string'
                                ? displayValue
                                : displayValue !== undefined && displayValue !== null
                                  ? JSON.stringify(displayValue, null, 2)
                                  : ''
                            }
                            placeholder={placeholder}
                            onChange={handleChange}
                            {...commonDropProps}
                          />
                        ) : (
                          <input
                            id={`param-${key}`}
                            type={isBoolean ? 'text' : param.type === 'number' || param.type === 'integer' ? 'number' : 'text'}
                            value={displayValue}
                            placeholder={placeholder}
                            onChange={handleChange}
                            {...commonDropProps}
                          />
                        )}

                        {isBoolean && (
                          <div className="boolean-quick-toggle">
                            <button
                              type="button"
                              className={staticValue === true && mappingValue === undefined ? 'boolean-toggle-btn active' : 'boolean-toggle-btn'}
                              onClick={() => handleParamChange(key, true)}
                            >
                              True
                            </button>
                            <button
                              type="button"
                              className={staticValue === false && mappingValue === undefined ? 'boolean-toggle-btn active' : 'boolean-toggle-btn'}
                              onClick={() => handleParamChange(key, false)}
                            >
                              False
                            </button>
                          </div>
                        )}

                        {param.options && param.options.length > 0 && !isBoolean && (
                          <select
                            className="parameter-options-select"
                            value={typeof staticValue === 'string' ? staticValue : ''}
                            onChange={e => handleParamChange(key, e.target.value)}
                          >
                            <option value="">Seçin...</option>
                            {param.options.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        )}

                        <div className="data-mapping-help">
                          Bu alan tek: statik değer yazabilir veya soldan bir path sürükleyip bırakabilirsin. <br />
                          <strong>Path</strong> ile başlayan değerler (örn. <code> $.result.output.chatInput</code>) otomatik olarak mapping olarak yorumlanır.
                          {isBoolean && (
                            <>
                              <br />
                              True/False için yukarıdaki butonları kullanabilirsin.
                            </>
                          )}
                        </div>

                        {paramError && <div className="param-error-text">{paramError}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT */}
              <div className="parameter-column parameter-column-right">
                <div className="parameter-section">
                  <h4>Node Çıktısı</h4>
                  {currentOutput ? (
                    <pre className="node-output-viewer">{safeStringify(currentOutput)}</pre>
                  ) : (
                    <div className="node-output-empty">Bu node henüz çalıştırılmamış veya çıktı bulunamadı.</div>
                  )}

                  <div className="execute-node-section">
                    <button
                      className={`btn btn-primary execute-node-button ${executingNodeId === currentNode.id ? 'executing' : ''}`}
                      onClick={() => executeSingleNode(currentNode.id)}
                      disabled={executingNodeId === currentNode.id}
                    >
                      <TbPlayerPlay size={16} />
                      {executingNodeId === currentNode.id ? 'Çalışıyor...' : "Sadece Bu Node'u Çalıştır"}
                    </button>

                    <div className="execute-node-hint">
                      Bu buton sadece bu node'u test eder. Akışı yukarıdan aşağıya yürütmek için ana
                      &quot;Akışı Başlat&quot; butonunu kullan.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="parameter-modal-footer">
            <button className="btn btn-primary" onClick={() => setShowParameterModal(false)}>
              <TbCheck size={16} /> Kaydet ve Kapat
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* =========================
     Render
  ========================= */
  return (
    <div className="automation-container">
      <ModuleSidebar
        title={'Automation'}
        menus={menus}
        selectedSubmenu={selectedSubmenu}
        onSubmenuClick={handleSubmenuClick}
        selectedFolderId={selectedFolderId}
        onFolderClick={setSelectedFolderId}
        showSearch={true}
      />

      <div className="automation-content">
        <div className="automation-inner-content">

          {/* Workflow header (title + actions) */}
          <div className="workflow-header-bar">
            <div className="workflow-header-left">
              {/* ✅ "Workflow Yok / Açık" yerine direkt title. Tıklayınca edit. */}
              {!isTitleEditing ? (
                <div
                  className="workflow-title-display"
                  title="Düzenlemek için tıkla"
                  onClick={() => setIsTitleEditing(true)}
                >
                  {(workflowTitle || '').trim()
                    ? workflowTitle
                    : 'Untitled Workflow'}
                </div>
              ) : (
                <input
                  className="workflow-title-input"
                  autoFocus
                  value={workflowTitle}
                  placeholder="Workflow title..."
                  onChange={e => setWorkflowTitle(e.target.value)}
                  onBlur={() => setIsTitleEditing(false)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') setIsTitleEditing(false);
                    if (e.key === 'Escape') setIsTitleEditing(false);
                  }}
                  title="Enter ile bitir"
                />
              )}

              {workflowID && (
                <div className="workflow-id-chip" title="workflowID">
                  #{workflowID}
                </div>
              )}
            </div>

            <div className="workflow-header-right">
              {/* ✅ Autosave spinner */}
              {isAutosaving && <div className="note-book-spinner"></div>}

              <button
                className="workflow-header-btn"
                type="button"
                onClick={manualSaveWorkflow}
                title={workflowID ? 'Kaydet (update)' : 'Kaydet (create)'}
              >
                <TbCheck size={16} />
                {workflowID ? 'Kaydet' : 'Oluştur'}
              </button>

              <button
                className="workflow-header-btn danger"
                type="button"
                onClick={() => deleteWorkflow()}
                disabled={!workflowID}
                title={!workflowID ? 'Açık workflow yok' : 'Workflow sil'}
              >
                <TbTrash size={16} />
                Sil
              </button>
            </div>
          </div>

          <div
            className="node-canvas"
            ref={canvasRef}
            onWheel={handleWheel}
            onMouseDown={handleCanvasMouseDown}
          >
            <button className="arrange-button" onClick={arrangeNodes} title="Arrange Nodes">
              <TbArrowsMaximize size={16} /> Arrange
            </button>

            <div
              className="nodes-wrapper"
              style={{ transform: `scale(${zoom})`, transformOrigin: '0 0' }}
            >
              <svg className="connections-svg">
                <defs>
                  <marker
                    id="arrowhead-selected"
                    markerWidth="10"
                    markerHeight="7"
                    refX="9"
                    refY="3.5"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3.5, 0 7" fill="#007bff" />
                  </marker>
                </defs>

                {edges.map(edge => {
                  const s = nodes.find(n => n.id === edge.sourceNodeId);
                  const t = nodes.find(n => n.id === edge.targetNodeId);
                  if (!s || !t) return null;

                  const sp = getNodeOutputPosition(s);
                  const tp = getNodeInputPosition(t);
                  const sel = selectedEdgeIds.has(edge.id);

                  const midX = (sp.x + tp.x) / 2;
                  const midY = (sp.y + tp.y) / 2;
                  const ang = Math.atan2(tp.y - sp.y, tp.x - sp.x);

                  return (
                    <g key={edge.id}>
                      <line
                        x1={sp.x}
                        y1={sp.y}
                        x2={tp.x}
                        y2={tp.y}
                        stroke={sel ? '#007bff' : '#999'}
                        strokeWidth={sel ? '3' : '2'}
                      />
                      <polygon
                        points={`${midX},${midY} ${midX - 8 * Math.cos(ang - Math.PI / 6)},${midY - 8 * Math.sin(ang - Math.PI / 6)} ${midX - 8 * Math.cos(ang + Math.PI / 6)},${midY - 8 * Math.sin(ang + Math.PI / 6)}`}
                        fill={sel ? '#007bff' : '#999'}
                      />
                    </g>
                  );
                })}

                <line
                  ref={connectionLineRef}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="0"
                  stroke="#007bff"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  markerEnd="url(#arrowhead-selected)"
                  style={{ display: 'none' }}
                />
              </svg>

              {nodes.map(node => (
                <div
                  key={node.id}
                  className={`node-box
                    ${selectedNodeIds.has(node.id) ? ' selected' : ''}
                    ${nodeErrors[node.id] ? ' error' : ''}
                    ${executingNodeId === node.id ? ' executing' : ''}`}
                  style={{ left: node.x, top: node.y }}
                  onDoubleClick={() => handleNodeParameterEdit(node)}
                  onMouseDown={e => handleMouseDown(e, node)}
                >
                  <div className="node-output" onMouseDown={e => handleNodeOutputMouseDown(e, node.id)} />
                  <div className="node-title">{node.type}</div>
                  <div className="node-input" onMouseUp={e => handleNodeInputMouseUp(e, node.id)} />

                  <button
                    className="node-settings-btn"
                    onClick={e => {
                      e.stopPropagation();
                      handleNodeParameterEdit(node);
                    }}
                  >
                    <TbSettings size={14} />
                  </button>

                  {Object.keys(node.dataMappings || {}).length > 0 && (
                    <div className="data-mapping-indicator">
                      <TbLink size={12} />
                    </div>
                  )}
                </div>
              ))}

              {selectionBox && (
                <div
                  className="selection-box"
                  style={{
                    left: Math.min(selectionBox.startX, selectionBox.endX),
                    top: Math.min(selectionBox.startY, selectionBox.endY),
                    width: Math.abs(selectionBox.endX - selectionBox.startX),
                    height: Math.abs(selectionBox.endY - selectionBox.startY)
                  }}
                />
              )}
            </div>
          </div>

          {executionError && <div className="execution-error">Hata: {executionError}</div>}

          <div className="execution-controls">
            {/* Workflowlarım button */}
            <button
              className="terminal-toggle-button"
              type="button"
              onClick={() => setWorkflowModalOpen(true)}
            >
              <TbCloud size={18} />
              Workflowlarım
            </button>

            <button
              className={`execute-button ${isExecuting ? 'executing' : ''}`}
              onClick={executeFlow}
              disabled={isExecuting || nodes.length === 0}
            >
              <TbPlayerPlay size={18} />
              {isExecuting ? 'Çalışıyor...' : 'Akışı Başlat'}
            </button>

            <button
              className="terminal-toggle-button"
              type="button"
              onClick={() => setShowTerminal(prev => !prev)}
            >
              <TbTerminal2 size={18} />
              {showTerminal ? 'Terminali Gizle' : 'Terminali Göster'}
            </button>
          </div>
        </div>

        {showTerminal && (
          <div
            className="automation-terminal"
            style={{
              left: `${terminalPos.x}px`,
              top: `${terminalPos.y}px`,
              width: `${terminalSize.width}px`,
              height: `${terminalSize.height}px`
            }}
          >
            <div className="automation-terminal-header" onMouseDown={handleTerminalMouseDown}>
              <span>Automation Terminal</span>
              <div className="automation-terminal-actions">
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setLogs([]);
                  }}
                >
                  Temizle
                </button>
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    setShowTerminal(false);
                  }}
                >
                  <TbX size={14} />
                </button>
              </div>
            </div>

            <div ref={terminalBodyRef} className="automation-terminal-body">
              {logs.length === 0 ? (
                <div className="terminal-empty">Henüz log yok.</div>
              ) : (
                logs.map(log => (
                  <div key={log.id} className={`terminal-line level-${log?.level?.toLowerCase()}`}>
                    <span className="terminal-time">{log.time}</span>
                    <span className="terminal-level">[{log.level}]</span>
                    <span className="terminal-label">{' '} {log.label}</span>
                    {log.message && <span className="terminal-message">{' '} {log.message}</span>}
                  </div>
                ))
              )}
            </div>

            <div className="terminal-resize-handle terminal-resize-handle-right" onMouseDown={e => handleTerminalResizeMouseDown(e, 'right')} />
            <div className="terminal-resize-handle terminal-resize-handle-left" onMouseDown={e => handleTerminalResizeMouseDown(e, 'left')} />
            <div className="terminal-resize-handle terminal-resize-handle-bottom" onMouseDown={e => handleTerminalResizeMouseDown(e, 'bottom')} />
            <div className="terminal-resize-handle terminal-resize-handle-top" onMouseDown={e => handleTerminalResizeMouseDown(e, 'top')} />

            <div className="terminal-resize-handle terminal-resize-handle-bottom-right" onMouseDown={e => handleTerminalResizeMouseDown(e, 'bottom-right')} />
            <div className="terminal-resize-handle terminal-resize-handle-bottom-left" onMouseDown={e => handleTerminalResizeMouseDown(e, 'bottom-left')} />
            <div className="terminal-resize-handle terminal-resize-handle-top-right" onMouseDown={e => handleTerminalResizeMouseDown(e, 'top-right')} />
            <div className="terminal-resize-handle terminal-resize-handle-top-left" onMouseDown={e => handleTerminalResizeMouseDown(e, 'top-left')} />
          </div>
        )}
      </div>

      {renderParameterModal()}
      {renderWorkflowModal()}
    </div>
  );
}